import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../services/auth';
import apiService from '../../services/api';
import { ChevronRight } from 'lucide-react';
import './FacilitiesList.css';

const FacilitiesList = () => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadFacilities();
  }, []);

  const loadFacilities = async () => {
    try {
      setLoading(true);
      const response = await apiService.getFacilities();
      setFacilities(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load facilities:', err);
      setError('Failed to load facilities');
    } finally {
      setLoading(false);
    }
  };

  const getTotalActivities = (facility) => {
    if (!facility.activities) return 0;
    return Object.values(facility.activities).reduce((sum, activities) => sum + activities.length, 0);
  };

  const formatStageName = (stage) => {
    return stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Calculate stats
  const totalFacilities = facilities.length;
  const manageableFacilities = facilities.filter(f => f.can_manage).length;
  const totalActivities = facilities.reduce((sum, f) => sum + getTotalActivities(f), 0);

  if (loading) {
    return (
      <div className="facilities-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading facilities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="facilities-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title-section">
          <h1>Facilities</h1>
          <p>Available facilities for LCA calculations</p>
        </div>
      </div>

      {/* Stats Bar - Fixed Layout */}
      <div className="stats-bar-fixed">
        <div className="stat-item-fixed">
          <div className="stat-value-fixed">{totalFacilities}</div>
          <div className="stat-label-fixed">Total Facilities</div>
        </div>
        <div className="stat-item-fixed">
          <div className="stat-value-fixed">{manageableFacilities}</div>
          <div className="stat-label-fixed">Manageable</div>
        </div>
        <div className="stat-item-fixed">
          <div className="stat-value-fixed">{totalActivities}</div>
          <div className="stat-label-fixed">Total Activities</div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={loadFacilities} className="btn btn-secondary">
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {facilities.length === 0 && !loading && (
        <div className="empty-state">
          <div className="empty-icon">🏭</div>
          <h2>No Facilities Found</h2>
          <p>No facilities are currently available.</p>
        </div>
      )}

      {/* Facilities List - Collapsible */}
      {facilities.length > 0 && (
        <div className="facilities-list">
          {facilities.map((facility) => (
            <details key={facility.id} className="facility-item-collapsible">
              <summary className="facility-summary">
                <div className="facility-summary-content">
                  <div className="facility-name-section">
                    <h3>{facility.name}</h3>
                    {facility.can_manage && (
                      <span className="badge badge-success">Can Manage</span>
                    )}
                  </div>
                  <div className="facility-quick-stats">
                    <span className="quick-stat">{getTotalActivities(facility)} activities</span>
                    <span className="quick-stat">
                      {facility.activities ? Object.keys(facility.activities).length : 0} stages
                    </span>
                  </div>
                </div>
                <ChevronRight className="chevron" size={20} />
              </summary>

              <div className="facility-details">
                {/* Facility Stats */}
                <div className="facility-stats-inline">
                  <div className="stat-inline">
                    <span className="stat-inline-label">Total Activities:</span>
                    <span className="stat-inline-value">{getTotalActivities(facility)}</span>
                  </div>
                  <div className="stat-inline">
                    <span className="stat-inline-label">Activity Stages:</span>
                    <span className="stat-inline-value">
                      {facility.activities ? Object.keys(facility.activities).length : 0}
                    </span>
                  </div>
                </div>

                {/* Activity Stages */}
                {facility.activities && (
                  <div className="stages-section">
                    <h4>Available Activity Stages:</h4>
                    <div className="stages-list">
                      {Object.entries(facility.activities).map(([stage, activities]) => (
                        <div key={stage} className="stage-item-compact">
                          <div className="stage-header-compact">
                            <span className="stage-name-compact">{formatStageName(stage)}</span>
                            <span className="badge badge-secondary">
                              {activities.length} {activities.length === 1 ? 'activity' : 'activities'}
                            </span>
                          </div>
                          {activities.length > 0 && (
                            <div className="stage-activities-compact">
                              {activities.slice(0, 5).map((activity, idx) => (
                                <span key={idx} className="activity-tag-compact">
                                  {typeof activity === 'string' ? activity : activity.field}
                                </span>
                              ))}
                              {activities.length > 5 && (
                                <span className="activity-tag-compact more">
                                  +{activities.length - 5} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <div className="facility-actions-inline">
                  <button 
                    onClick={() => navigate(`/scenarios/create?facility=${facility.name}`)}
                    className="btn btn-primary btn-large"
                  >
                    Create Scenario with this Facility
                  </button>
                </div>
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

export default FacilitiesList;
